import React from 'react';
import { View, Image, StyleSheet, Text, Platform } from 'react-native';
import { useTheme } from '../../hooks/useTheme';

interface UserAvatarProps {
  imageUrl?: string | null;
  name?: string;
  email?: string;
  size?: number;
  style?: Record<string, unknown>;
  showActiveStatus?: boolean;
  isActive?: boolean;
}

/**
 * A component that displays the user's avatar or a default guest avatar
 * if the user is not authenticated
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
  imageUrl,
  name,
  email,
  size = 40,
  style,
  showActiveStatus = false,
  isActive = true,
}) => {
  const { colors } = useTheme();

  const avatarStyle = {
    width: size,
    height: size,
    borderRadius: size / 2,
  };

  const statusDotSize = Math.max(size * 0.25, 8);

  const renderAvatar = () => {
    if (imageUrl) {
      return (
        <Image
          source={{ uri: imageUrl }}
          style={[styles.avatar, avatarStyle]}
          resizeMode="cover"
        />
      );
    }

    // Get first letter of name (or email as fallback) for default avatar
    const getInitial = () => {
      if (name) {
        return name.charAt(0).toUpperCase();
      }
      if (email) {
        return email.charAt(0).toUpperCase();
      }
      return '?';
    };

    // Show placeholder when no image is available
    return (
      <View
        style={[
          styles.avatar,
          avatarStyle,
          {
            backgroundColor: colors.primary + '20',
            alignItems: 'center',
            justifyContent: 'center',
          },
        ]}
      >
        <Text
          style={[
            styles.initial,
            {
              color: colors.primary,
              fontSize: size * 0.4,
              fontWeight: '600',
            },
          ]}
        >
          {getInitial()}
        </Text>
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      {renderAvatar()}
      {showActiveStatus && isActive && (
        <View
          style={[
            styles.activeStatus,
            {
              width: statusDotSize,
              height: statusDotSize,
              borderRadius: statusDotSize / 2,
              bottom: 0,
              right: 0,
            },
          ]}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  avatar: {
    overflow: 'hidden',
    backgroundColor: '#F3F4F6',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  initial: {
    fontWeight: '600',
  },
  activeStatus: {
    position: 'absolute',
    backgroundColor: '#10B981',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 3px rgba(0,0,0,0.3)' }
      : {
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.3,
          shadowRadius: 3,
          elevation: 3,
        }),
  },
});

export default UserAvatar;
